import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  MessageCircle, Send, RefreshCw, LogOut, Paperclip,
  Search, Phone, User, CheckCheck, Check, ArrowLeft,
  Headphones, UserMinus, X, Wifi, WifiOff, Plus, Hash, Trash2, Edit3, Save, BookUser, ZoomIn, ZoomOut, RotateCcw,
  FileDown, Mic, Play, ChevronDown, Info, Clock, AlertCircle, MessageSquareText
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { it } from 'date-fns/locale';

// ── Design tokens ──
const C = {
  bg: '#FAFAFA', surface: '#FFFFFF', surfaceHover: '#F5F5F4', surfaceActive: '#E8ECEA',
  primary: '#4A6B5D', primaryHover: '#3D5A4E', accent: '#C28E72',
  border: '#E7E5E4', text: '#1C1917', textSec: '#78716C', textInv: '#FFFFFF',
  noteBg: '#FEF3C7', noteText: '#92400E', noteBorder: '#FDE68A',
};

// ── Formattazione WhatsApp (*grassetto* _corsivo_ ~barrato~) ──
const WA_TOKEN = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
const renderWaText = (text) => {
  if (text === null || text === undefined) return null;
  const parts = String(text).split(WA_TOKEN);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.length > 1 && p.startsWith('*') && p.endsWith('*')) return <strong key={i}>{p.slice(1, -1)}</strong>;
    if (p.length > 1 && p.startsWith('_') && p.endsWith('_')) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.length > 1 && p.startsWith('~') && p.endsWith('~')) return <span key={i} style={{ textDecoration: 'line-through' }}>{p.slice(1, -1)}</span>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};
const stripWaMarkers = (text) => text ? String(text).replace(/[*_~`]/g, '') : text;

// ── Numeri / identificatori WhatsApp ──
const isLidId = (id) => String(id || '').includes('@lid');
const formatWaNumber = (id) => {
  const raw = String(id || '').split('@')[0].replace(/[^0-9]/g, '');
  if (!raw || raw.length < 8) return '';
  let n = raw, prefix = '';
  if (n.startsWith('39') && n.length >= 11) { prefix = '+39 '; n = n.slice(2); }
  const g = n.replace(/(\d{3})(\d{3})(\d+)/, '$1 $2 $3');
  return (prefix + g).trim();
};
const getChatNumberLabel = (id) => {
  if (isLidId(id)) return 'Numero non disponibile';
  return formatWaNumber(id) || 'Numero non disponibile';
};
const resolveChatName = (chat, contactName) => {
  if (contactName) return contactName;
  const nm = chat?.name;
  if (nm && /[a-zA-Z]/.test(nm)) return nm;
  return getChatNumberLabel(chat?.id);
};

// ── Risposte rapide (modelli) ──
const QUICK_REPLIES = [
  { label: 'Saluto', text: "Buongiorno, sono dell'Agenzia Schilirò. Come posso aiutarla?" },
  { label: 'Conferma ricezione', text: 'Buongiorno, confermiamo di aver ricevuto. La ricontattiamo a breve con tutti i dettagli. Grazie.' },
  { label: 'Appuntamento', text: "Buongiorno, quando preferisce passare in agenzia? Siamo aperti dal lunedì al venerdì. Ci faccia sapere l'orario più comodo." },
];

// ── Spunte di stato messaggio ──
const MsgTicks = ({ msg, dark }) => {
  const base = dark ? '#BBF7D0' : '#9CA3AF';
  if (msg.status === 'sending') return <Clock size={14} style={{ color: base }} />;
  const ack = typeof msg.ack === 'number' ? msg.ack : null;
  if (ack !== null) {
    if (ack >= 3) return <CheckCheck size={15} style={{ color: dark ? '#93C5FD' : '#3B82F6' }} />;
    if (ack === 2) return <CheckCheck size={15} style={{ color: base }} />;
    return <Check size={15} style={{ color: base }} />;
  }
  return <Check size={15} style={{ color: base }} />;
};
const ackLabel = (msg) => {
  if (msg.status === 'sending') return 'Invio in corso';
  if (msg.status === 'failed') return 'Non inviato';
  const ack = typeof msg.ack === 'number' ? msg.ack : null;
  if (ack !== null) { if (ack >= 3) return 'Letto'; if (ack === 2) return 'Consegnato'; }
  return 'Inviato';
};

// ── Chat List Item ──
const ChatListItem = ({ chat, isSelected, onClick, currentUserId, contactName, showAssignment }) => {
  const displayName = resolveChatName(chat, contactName);
  const isNumberName = !/[a-zA-Z]/.test(displayName || '');
  const getInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';
  const getTimeLabel = (ts) => {
    if (!ts) return '';
    const d = new Date(ts * 1000);
    if (isToday(d)) return format(d, 'HH:mm');
    if (isYesterday(d)) return 'Ieri';
    return format(d, 'dd/MM', { locale: it });
  };
  const isMine = chat.assignedTo === currentUserId;
  const isAssigned = !!chat.assignedTo;
  const hasUnread = chat.unreadCount > 0;
  const assignLabel = !isAssigned ? 'Libera' : isMine ? 'Tu' : (chat.assignedToName?.split(' ')[0] || 'Assegnata');
  const assignColors = !isAssigned ? { bg: '#FEF3C7', fg: '#92400E' } : isMine ? { bg: '#E8ECEA', fg: C.primary } : { bg: '#F3E4D8', fg: C.accent };

  return (
    <button
      data-testid={`chat-item-${chat.id}`}
      onClick={onClick}
      className="w-full text-left px-3 py-3 flex items-start gap-3 rounded-xl mx-1 transition-all duration-150 active:scale-[0.99]"
      style={{
        width: 'calc(100% - 8px)',
        backgroundColor: isSelected ? C.surfaceActive : 'transparent',
      }}
      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = C.surfaceHover; }}
      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <div className="relative flex-shrink-0 mt-0.5">
        <Avatar className="h-11 w-11">
          <AvatarFallback className="text-xs font-semibold" style={{
            backgroundColor: hasUnread ? '#E8ECEA' : '#F5F5F4',
            color: hasUnread ? C.primary : C.textSec,
            fontFamily: 'Manrope, sans-serif',
          }}>
            {chat.isGroup ? <Hash size={16} /> : (isNumberName ? <User size={16} /> : getInitials(displayName))}
          </AvatarFallback>
        </Avatar>
        {hasUnread && (
          <span className="absolute -top-1 -right-1 h-5 min-w-[20px] rounded-full text-[10px] font-bold flex items-center justify-center px-1.5 shadow-md ring-2 ring-white"
            style={{ backgroundColor: C.accent, color: C.textInv }} data-testid={`unread-badge-${chat.id}`}>
            {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-1.5 min-w-0">
          <span className="text-[13px] leading-snug" title={displayName} style={{
            flex: '1 1 0%', minWidth: 0, wordBreak: 'break-word',
            fontWeight: hasUnread ? 700 : 500,
            color: C.text,
            fontFamily: 'Manrope, sans-serif',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {displayName}
          </span>
          <span className="text-[10px] flex-shrink-0 whitespace-nowrap mt-0.5" style={{
            color: hasUnread ? C.accent : '#A8A29E',
            fontWeight: hasUnread ? 700 : 400,
          }}>
            {getTimeLabel(chat.lastMessage?.timestamp || chat.timestamp)}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
          {chat.lastMessage?.fromMe && <CheckCheck size={13} style={{ color: C.primary }} className="flex-shrink-0" />}
          <p className="text-[12px] truncate flex-1 min-w-0" style={{
            color: hasUnread ? C.text : '#A8A29E',
            fontWeight: hasUnread ? 500 : 400,
          }}>
            {stripWaMarkers(chat.lastMessage?.body) || 'Nessun messaggio'}
          </p>
        </div>
        {showAssignment && (
          <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: assignColors.bg, color: assignColors.fg }} data-testid={`assign-badge-${chat.id}`}>
            {isAssigned && <User size={9} />}{assignLabel}
          </span>
        )}
      </div>
    </button>
  );
};

// ── Media Renderer ──
const MediaContent = ({ msg, onImageClick, chatId, onMediaLoaded }) => {
  if (!msg.hasMedia) return null;
  const mediaUrl = msg.media ? `data:${msg.media.mimetype};base64,${msg.media.data}` : null;
  const type = msg.type;
  const filename = msg.media?.filename || msg.body || 'file';
  const mime = msg.media?.mimetype || '';
  if (msg.mediaError || (msg.hasMedia && !msg.media)) {
    const labels = { audio: 'Audio', ptt: 'Messaggio vocale', document: 'Documento', video: 'Video', image: 'Immagine', sticker: 'Sticker' };
    return <DownloadMediaButton chatId={chatId} messageId={msg.id} type={type} label={labels[type] || 'Media'} filename={msg.body || 'file'} onMediaLoaded={onMediaLoaded} />;
  }
  if (!mediaUrl) return null;
  if (type === 'image' || type === 'sticker') {
    const maxW = type === 'sticker' ? 'max-w-[140px]' : 'max-w-[240px]';
    return (
      <div className="mb-1.5 relative group" data-testid={`media-thumb-${msg.id}`}>
        <button onClick={() => onImageClick(mediaUrl)} className="block cursor-pointer relative overflow-hidden rounded-xl">
          <img src={mediaUrl} alt="Media" className={`rounded-xl ${maxW} max-h-[200px] object-cover`} />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-xl" />
        </button>
        <a href={mediaUrl} download={filename || `foto_${msg.id?.slice(-6)}.jpg`}
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg shadow-md"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
          data-testid={`download-img-${msg.id}`}>
          <FileDown size={14} style={{ color: C.primary }} />
        </a>
      </div>
    );
  }
  if (type === 'audio' || type === 'ptt') return <AudioWithTranscription msg={msg} mediaUrl={mediaUrl} mime={mime} type={type} />;
  if (type === 'video') {
    return (
      <div className="mb-1.5 rounded-xl overflow-hidden relative group" data-testid={`media-video-${msg.id}`}>
        <video controls preload="metadata" className="max-w-[260px] max-h-[200px] rounded-xl"><source src={mediaUrl} type={mime} /></video>
        <a href={mediaUrl} download={filename || `video_${msg.id?.slice(-6)}.mp4`}
          onClick={(e) => e.stopPropagation()}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg shadow-md"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
          data-testid={`download-video-${msg.id}`}>
          <FileDown size={14} style={{ color: C.primary }} />
        </a>
      </div>
    );
  }
  if (type === 'document') {
    const isPdf = mime.includes('pdf');
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1.5" style={{ backgroundColor: 'rgba(255,255,255,0.6)', border: `1px solid ${C.border}` }} data-testid={`media-doc-${msg.id}`}>
        <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isPdf ? '#FEE2E2' : '#DBEAFE' }}>
          <FileDown size={18} className={isPdf ? 'text-red-600' : 'text-blue-600'} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate" style={{ color: C.text }}>{filename}</p>
          <p className="text-[10px] uppercase" style={{ color: C.textSec }}>{isPdf ? 'PDF' : mime.split('/').pop()}</p>
        </div>
        <a href={mediaUrl} download={filename} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold flex-shrink-0 transition-colors active:scale-95" style={{ backgroundColor: C.primary, color: C.textInv }} data-testid={`download-doc-${msg.id}`}><FileDown size={13} /> Scarica</a>
      </div>
    );
  }
  return null;
};

// ── Download Media On-Demand Button ──
const DownloadMediaButton = ({ chatId, messageId, type, label, filename, onMediaLoaded }) => {
  const [downloading, setDownloading] = useState(false);
  const [mediaData, setMediaData] = useState(null);
  const [error, setError] = useState(false);
  const [transcription, setTranscription] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const isPdf = filename?.toLowerCase().endsWith('.pdf');

  const transcribeAudio = async (data) => {
    setTranscribing(true);
    try {
      const res = await api.post('/whatsapp/transcribe-audio', { message_id: messageId, audio_data: data.data, mimetype: data.mimetype });
      if (res.data.transcription) setTranscription(res.data.transcription);
      else if (res.data.error) setTranscription('[Trascrizione non disponibile]');
    } catch { setTranscription('[Errore trascrizione]'); }
    finally { setTranscribing(false); }
  };

  const handleDownload = async () => {
    setDownloading(true); setError(false);
    try {
      const res = await api.get(`/whatsapp/chat/${encodeURIComponent(chatId)}/media/${encodeURIComponent(messageId)}`);
      if (res.data.error) setError(true);
      else {
        setMediaData(res.data);
        if (onMediaLoaded) onMediaLoaded(messageId, res.data);
        if ((type === 'audio' || type === 'ptt') && res.data.data) transcribeAudio(res.data);
      }
    } catch { setError(true); }
    finally { setDownloading(false); }
  };

  if (mediaData) {
    const url = `data:${mediaData.mimetype};base64,${mediaData.data}`;
    const mime = mediaData.mimetype || '';
    if (type === 'audio' || type === 'ptt') {
      return (
        <div className="mb-1.5 space-y-1.5" data-testid={`audio-player-${messageId}`}>
          <div className={type === 'ptt' ? 'flex items-center gap-2' : ''}>
            {type === 'ptt' && <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.surfaceActive }}><Mic size={14} style={{ color: C.primary }} /></div>}
            <audio controls preload="auto" className="h-9 max-w-[240px]"><source src={url} type={mime} /></audio>
          </div>
          {transcribing && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <RefreshCw size={12} className="animate-spin text-blue-500" />
              <span className="text-[11px] font-medium text-blue-600">Trascrizione AI in corso...</span>
            </div>
          )}
          {transcription && (
            <div className="px-3 py-2.5 rounded-xl border-l-3" style={{ backgroundColor: '#F0FDF4', borderLeft: `3px solid ${C.primary}` }} data-testid={`transcription-${messageId}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.primary }}>Trascrizione AI</p>
              <p className="text-xs leading-relaxed" style={{ color: C.text }}>{transcription}</p>
            </div>
          )}
        </div>
      );
    }
    if (type === 'image' || type === 'sticker') {
      return (
        <div className="mb-1.5 relative group">
          <img src={url} alt="Media" className="rounded-xl max-w-[240px] max-h-[200px] object-cover" />
          <a href={url} download={filename || `foto_${messageId?.slice(-6)}.jpg`}
            className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg shadow-md"
            style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
            data-testid={`download-img-dl-${messageId}`}>
            <FileDown size={14} style={{ color: C.primary }} />
          </a>
        </div>
      );
    }
    if (type === 'video') {
      return (
        <div className="mb-1.5 relative group">
          <video controls preload="metadata" className="max-w-[260px] max-h-[200px] rounded-xl mb-1.5"><source src={url} type={mime} /></video>
          <a href={url} download={filename || `video_${messageId?.slice(-6)}.mp4`}
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg shadow-md"
            style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
            data-testid={`download-video-dl-${messageId}`}>
            <FileDown size={14} style={{ color: C.primary }} />
          </a>
        </div>
      );
    }
    const fn = mediaData.filename || filename || 'file';
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1.5" style={{ backgroundColor: 'rgba(255,255,255,0.6)', border: `1px solid ${C.border}` }}>
        <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isPdf ? '#FEE2E2' : '#DBEAFE' }}><FileDown size={18} className={isPdf ? 'text-red-600' : 'text-blue-600'} /></div>
        <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate" style={{ color: C.text }}>{fn}</p><p className="text-[10px] uppercase" style={{ color: C.textSec }}>{mime.split('/').pop()}</p></div>
        <a href={url} download={fn} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold flex-shrink-0 active:scale-95" style={{ backgroundColor: C.primary, color: C.textInv }}><FileDown size={13} /> Scarica</a>
      </div>
    );
  }

  return (
    <button onClick={handleDownload} disabled={downloading}
      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl mb-1.5 transition-all w-full text-left"
      style={{ backgroundColor: C.surfaceHover, border: error ? '1px solid #FCA5A5' : '1px solid transparent' }}
      data-testid={`download-media-${messageId}`}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${downloading ? 'animate-pulse' : ''}`} style={{ backgroundColor: C.surface }}>
        {downloading ? <RefreshCw size={16} className="animate-spin" style={{ color: C.textSec }} /> : <FileIcon type={type} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium" style={{ color: C.text }}>{downloading ? 'Scaricamento...' : error ? `${label} - Riprova` : `${label} - Tocca per scaricare`}</p>
        {filename && filename !== 'file' && <p className="text-[10px] truncate" style={{ color: C.textSec }}>{filename}</p>}
      </div>
      {!downloading && <FileDown size={14} style={{ color: error ? '#EF4444' : C.primary }} />}
    </button>
  );
};

const FileIcon = ({ type }) => {
  switch (type) {
    case 'audio': case 'ptt': return <Mic size={14} style={{ color: C.primary }} />;
    case 'document': return <FileDown size={14} className="text-blue-500" />;
    case 'video': return <Play size={14} className="text-purple-500" />;
    default: return <Paperclip size={14} style={{ color: C.textSec }} />;
  }
};

// ── Audio with Transcription ──
const AudioWithTranscription = ({ msg, mediaUrl, mime, type }) => {
  const [transcription, setTranscription] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const hasStarted = useRef(false);
  useEffect(() => {
    if (hasStarted.current || !msg.media?.data) return;
    hasStarted.current = true;
    (async () => {
      setTranscribing(true);
      try {
        const res = await api.post('/whatsapp/transcribe-audio', { message_id: msg.id, audio_data: msg.media.data, mimetype: msg.media.mimetype });
        if (res.data.transcription) setTranscription(res.data.transcription);
        else setTranscription('[Trascrizione non disponibile per questo audio]');
      } catch { setTranscription('[Trascrizione non riuscita, riprova più tardi]'); } finally { setTranscribing(false); }
    })();
  }, [msg.id, msg.media]);
  return (
    <div className="mb-1.5 space-y-1.5" data-testid={`media-audio-${msg.id}`}>
      <div className={type === 'ptt' ? 'flex items-center gap-2' : ''}>
        {type === 'ptt' && <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.surfaceActive }}><Mic size={14} style={{ color: C.primary }} /></div>}
        <audio controls preload="metadata" className="h-9 max-w-[240px]"><source src={mediaUrl} type={mime} /></audio>
      </div>
      {transcribing && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
          <RefreshCw size={12} className="animate-spin text-blue-500" />
          <span className="text-[11px] font-medium text-blue-600">Trascrizione AI in corso...</span>
        </div>
      )}
      {transcription && (
        <div className="px-3 py-2.5 rounded-xl" style={{ backgroundColor: '#F0FDF4', borderLeft: `3px solid ${C.primary}` }} data-testid={`transcription-${msg.id}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.primary }}>Trascrizione AI</p>
          <p className="text-xs leading-relaxed" style={{ color: C.text }}>{transcription}</p>
        </div>
      )}
    </div>
  );
};

// ── Message Bubble ──
const MessageBubble = ({ msg, formatTime, onImageClick, chatId, onRetry }) => {
  const failed = msg.status === 'failed';
  return (
    <div className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'} mb-2`}>
      <div className="max-w-[78%] sm:max-w-[65%] px-3.5 py-2 shadow-sm" style={{
        backgroundColor: msg.fromMe ? (failed ? '#FEE2E2' : C.primary) : C.surface,
        color: msg.fromMe ? (failed ? '#991B1B' : C.textInv) : C.text,
        borderRadius: msg.fromMe ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
        border: msg.fromMe ? (failed ? '1px solid #FCA5A5' : 'none') : `1px solid ${C.border}`,
        fontFamily: 'Manrope, sans-serif',
      }}>
        {!msg.fromMe && msg.contact?.name && (
          <p className="text-[11px] font-semibold mb-0.5" style={{ color: C.accent }}>{msg.contact.name}</p>
        )}
        <MediaContent msg={msg} onImageClick={onImageClick} chatId={chatId} />
        {msg.body && <p className="text-[13px] leading-relaxed whitespace-pre-wrap break-words">{renderWaText(msg.body)}</p>}
        <div className="flex items-center justify-end gap-1 mt-1" style={{ opacity: failed ? 1 : 0.75 }}>
          <span className="text-[10px]">{formatTime(msg.timestamp)}</span>
          {msg.fromMe && !failed && (
            <Tooltip><TooltipTrigger asChild><span className="inline-flex" data-testid={`msg-status-${msg.id}`}><MsgTicks msg={msg} dark /></span></TooltipTrigger><TooltipContent>{ackLabel(msg)}</TooltipContent></Tooltip>
          )}
          {msg.fromMe && failed && <AlertCircle size={14} style={{ color: '#DC2626' }} />}
        </div>
        {failed && (
          <div className="flex items-center justify-end gap-2 mt-1">
            <span className="text-[10px] font-medium" style={{ color: '#B91C1C' }}>Non inviato</span>
            <button onClick={() => onRetry && onRetry(msg)} className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold active:scale-95" style={{ backgroundColor: '#DC2626', color: '#FFF' }} data-testid={`retry-msg-${msg.id}`}><RefreshCw size={11} /> Riprova</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ──
export const WhatsApp = () => {
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState({ isReady: false, hasQR: false });
  const [qrCode, setQrCode] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeoutRef = useRef(null);
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [newContactNumber, setNewContactNumber] = useState('+39 ');
  const [newContactMessage, setNewContactMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [showContactPanel, setShowContactPanel] = useState(false);
  const [chatNotes, setChatNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [users, setUsers] = useState([]);
  const [contactsMap, setContactsMap] = useState({});
  const [editingContactName, setEditingContactName] = useState('');
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [pairingMode, setPairingMode] = useState(false);
  const [pairingPhone, setPairingPhone] = useState('+39');
  const [pairingCode, setPairingCode] = useState(null);
  const [pairingLoading, setPairingLoading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [chatNote, setChatNote] = useState('');
  const [chatNoteEditing, setChatNoteEditing] = useState(false);
  const [chatNoteDraft, setChatNoteDraft] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPos, setPanPos] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => { checkStatus(); loadUsers(); loadContacts(); const iv = setInterval(checkStatus, 15000); return () => clearInterval(iv); }, []);
  useEffect(() => { if (status.isReady) { loadChats(); const iv = setInterval(loadChats, 15000); return () => clearInterval(iv); } }, [status.isReady]);

  const pendingMessageRef = useRef(false);
  useEffect(() => {
    if (!status.isReady || chats.length === 0 || pendingMessageRef.current) return;
    const numero = searchParams.get('numero'); const messaggio = searchParams.get('messaggio');
    if (!numero || !messaggio) return;
    pendingMessageRef.current = true;
    const clean = numero.replace(/[^0-9]/g, '');
    const existingChat = chats.find(c => { const chatNum = (c.id || '').replace('@c.us', '').replace('@lid', '').replace(/[^0-9]/g, ''); return chatNum.endsWith(clean) || clean.endsWith(chatNum); });
    if (existingChat) { setSelectedChat(existingChat); setNewMessage(messaggio); }
    else { setSelectedChat({ id: `${clean}@c.us`, name: numero, isGroup: false }); setNewMessage(messaggio); }
    setSearchParams({}, { replace: true });
  }, [status.isReady, chats]);

  // Cache messaggi per chat veloce
  const msgCacheRef = useRef({});
  const selectedChatIdRef = useRef(null);

  useEffect(() => { if (selectedChat) {
    selectedChatIdRef.current = selectedChat.id;
    setMessages(msgCacheRef.current[selectedChat.id] || []);
    loadMessages(selectedChat.id); loadNotes(selectedChat.id); loadChatNote(selectedChat.id);
    markChatRead(selectedChat.id);
    const iv = setInterval(() => loadMessages(selectedChat.id), 8000); return () => clearInterval(iv);
  } }, [selectedChat?.id]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadUsers = async () => { try { const r = await api.get('/users'); setUsers(r.data); } catch {} };
  const loadContacts = async () => { try { const r = await api.get('/whatsapp/contacts'); const map = {}; (r.data || []).forEach(c => { map[c.chat_id] = c; }); setContactsMap(map); } catch {} };
  const saveContactName = async (chatId, nome) => { if (!nome.trim()) return; try { await api.post(`/whatsapp/contacts?chat_id=${encodeURIComponent(chatId)}&nome=${encodeURIComponent(nome.trim())}`); toast.success('Nome contatto salvato'); setIsEditingContact(false); loadContacts(); } catch { toast.error('Errore salvataggio contatto'); } };
  const deleteContactName = async (chatId) => { try { await api.delete(`/whatsapp/contacts/${encodeURIComponent(chatId)}`); toast.success('Nome contatto rimosso'); setIsEditingContact(false); setEditingContactName(''); loadContacts(); } catch { toast.error('Errore rimozione contatto'); } };
  const getContactDisplayName = (chat) => resolveChatName(chat, contactsMap[chat.id]?.nome);

  const checkStatus = async () => { try { const r = await api.get('/whatsapp/status'); setStatus(r.data); if (r.data.hasQR && !r.data.isReady) { const qr = await api.get('/whatsapp/qr'); setQrCode(qr.data.qrCode); } else setQrCode(null); } catch {} finally { setLoading(false); } };

  // QR polling rapido quando serve scansione
  useEffect(() => {
    if (status.hasQR && !status.isReady) {
      const qrIv = setInterval(async () => {
        try { const qr = await api.get('/whatsapp/qr'); setQrCode(qr.data.qrCode); } catch {}
      }, 5000);
      return () => clearInterval(qrIv);
    }
  }, [status.hasQR, status.isReady]);
  const loadChats = useCallback(async (searchTerm) => { try { const params = { filter: 'all' }; if (searchTerm) params.search = searchTerm; const r = await api.get('/whatsapp/chats', { params }); if (r.data.chats) setChats(r.data.chats); if (r.data.error) console.warn('WA chats warning:', r.data.error); } catch {} finally { setChatsLoaded(true); } }, []);
  const markChatRead = useCallback(async (chatId) => {
    if (!chatId) return;
    setChats(prev => prev.map(c => c.id === chatId ? { ...c, unreadCount: 0 } : c));
    try { await api.post(`/whatsapp/chat/mark-read?chat_id=${encodeURIComponent(chatId)}`); } catch {}
  }, []);
  const loadMessages = async (chatId) => {
    if (!msgCacheRef.current[chatId]) setMsgLoading(true);
    try {
      const r = await api.get(`/whatsapp/chat/${chatId}/messages`);
      if (chatId !== selectedChatIdRef.current) return;
      if (r.data.messages) {
        const serverMsgs = r.data.messages;
        // Mantieni i messaggi ottimistici (inviati dal CRM) non ancora confermati dal server,
        // così non spariscono durante il refresh. Deduplica per contenuto ed espira dopo 60s.
        const now = Math.floor(Date.now() / 1000);
        const sentBodies = new Set(serverMsgs.filter(m => m.fromMe).map(m => (m.body || '').trim()));
        const pendingOptimistic = (msgCacheRef.current[chatId] || []).filter(m =>
          String(m.id).startsWith('opt_') &&
          !sentBodies.has((m.body || '').trim()) &&
          (now - (m.timestamp || 0)) < 60
        );
        const merged = [...serverMsgs, ...pendingOptimistic];
        setMessages(merged);
        msgCacheRef.current[chatId] = merged;
      }
      if (r.data.error && (!r.data.messages || r.data.messages.length === 0)) toast.error(r.data.error);
    } catch { if (chatId === selectedChatIdRef.current) toast.error('Errore caricamento messaggi'); }
    finally { if (chatId === selectedChatIdRef.current) setMsgLoading(false); }
  };
  const loadChatNote = async (chatId) => { try { const r = await api.get(`/whatsapp/chat-note/${encodeURIComponent(chatId)}`); setChatNote(r.data.note || ''); setChatNoteDraft(r.data.note || ''); setChatNoteEditing(false); } catch { setChatNote(''); setChatNoteDraft(''); } };
  const saveChatNote = async () => { if (!selectedChat) return; try { await api.put(`/whatsapp/chat-note/${encodeURIComponent(selectedChat.id)}`, { text: chatNoteDraft }); setChatNote(chatNoteDraft); setChatNoteEditing(false); toast.success('Nota salvata'); } catch { toast.error('Errore salvataggio nota'); } };

  const handleAssign = async (chatId) => {
    try {
      const r = await api.post(`/whatsapp/chat/assign?chat_id=${chatId}`);
      if (r.data.success) { toast.success('Chat presa in carico!'); setChats(prev => prev.map(c => c.id === chatId ? { ...c, assignedTo: r.data.assignedTo, assignedToName: r.data.assignedToName } : c)); if (selectedChat?.id === chatId) setSelectedChat(prev => ({ ...prev, assignedTo: r.data.assignedTo, assignedToName: r.data.assignedToName })); setTimeout(loadChats, 300); }
    } catch { toast.error('Errore assegnazione'); }
  };
  const handleUnassign = async (chatId) => {
    try {
      const r = await api.post(`/whatsapp/chat/unassign?chat_id=${chatId}`);
      if (r.data.success) { toast.success('Chat rilasciata'); setChats(prev => prev.map(c => c.id === chatId ? { ...c, assignedTo: null, assignedToName: null } : c)); if (selectedChat?.id === chatId) setSelectedChat(prev => ({ ...prev, assignedTo: null, assignedToName: null })); setTimeout(loadChats, 300); }
    } catch { toast.error('Errore rilascio chat'); }
  };

  const loadNotes = async (chatId) => { try { const r = await api.get(`/whatsapp/notes/${encodeURIComponent(chatId)}`); setChatNotes(r.data || []); } catch { setChatNotes([]); } };
  const addNote = async () => { if (!newNote.trim() || !selectedChat) return; try { const r = await api.post(`/whatsapp/notes/${encodeURIComponent(selectedChat.id)}?testo=${encodeURIComponent(newNote.trim())}`); setChatNotes(prev => [r.data, ...prev]); setNewNote(''); toast.success('Nota aggiunta'); } catch { toast.error('Errore salvataggio nota'); } };
  const deleteNote = async (noteId) => { try { await api.delete(`/whatsapp/notes/${noteId}`); setChatNotes(prev => prev.filter(n => n.id !== noteId)); toast.success('Nota eliminata'); } catch { toast.error('Errore eliminazione'); } };

  const markMsgStatus = (id, status) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));
    const cid = selectedChatIdRef.current;
    const arr = msgCacheRef.current[cid];
    if (arr) msgCacheRef.current[cid] = arr.map(m => m.id === id ? { ...m, status } : m);
  };

  const _sendText = async (text) => {
    const res = await api.post('/whatsapp/send', { numero: selectedChat.id, messaggio: text });
    if (res.data?.error) throw new Error(res.data.error.includes('LID') ? 'Numero non raggiungibile. Invia prima un messaggio dal telefono.' : ('Errore invio: ' + res.data.error));
  };

  const retrySend = async (msg) => {
    markMsgStatus(msg.id, 'sending');
    try { await _sendText(msg.body); markMsgStatus(msg.id, 'sent'); setTimeout(() => loadMessages(selectedChat.id), 600); }
    catch (err) { markMsgStatus(msg.id, 'failed'); toast.error(err.message || 'Errore invio'); }
  };

  const handleSend = async (e) => {
    e.preventDefault(); if (!newMessage.trim() && !selectedFile) return;
    const msgText = newMessage;
    markChatRead(selectedChat.id);
    let optId = null;
    // Ottimistic UI: mostra il messaggio subito
    if (msgText.trim() && !selectedFile) {
      optId = `opt_${Date.now()}`;
      const optimistic = { id: optId, body: msgText, fromMe: true, timestamp: Math.floor(Date.now()/1000), type: 'chat', hasMedia: false, status: 'sending', contact: { name: 'Tu', number: '' } };
      setMessages(prev => [...prev, optimistic]);
      msgCacheRef.current[selectedChat.id] = [...(msgCacheRef.current[selectedChat.id] || []), optimistic];
    }
    setNewMessage(''); setQuickOpen(false);
    try {
      if (selectedFile) { const fd = new FormData(); fd.append('file', selectedFile); fd.append('to', selectedChat.id); fd.append('caption', msgText); await api.post('/whatsapp/send-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }
      else { await _sendText(msgText); if (optId) markMsgStatus(optId, 'sent'); }
      setTimeout(() => loadMessages(selectedChat.id), 600);
    } catch (err) {
      if (optId) markMsgStatus(optId, 'failed');
      toast.error(err.message || err.response?.data?.detail || 'Errore invio messaggio');
    }
  };

  const handleNewContact = async () => {
    const clean = newContactNumber.replace(/[^\d]/g, '');
    if (clean.length < 10) { toast.error('Inserisci un numero valido'); return; }
    const chatId = `${clean}@c.us`; const msg = newContactMessage.trim() || 'Buongiorno';
    try {
      const res = await api.post('/whatsapp/send', { numero: chatId, messaggio: msg });
      if (res.data?.error) { toast.error(res.data.error.includes('LID') ? 'Numero non raggiungibile via WhatsApp. Invia prima un messaggio dal telefono, poi potrai continuare dal CRM.' : res.data.error); return; }
      toast.success('Conversazione avviata!'); setNewContactOpen(false); setNewContactNumber('+39 '); setNewContactMessage(''); setTimeout(() => { loadChats(); setSelectedChat({ id: chatId, name: newContactNumber.trim(), isGroup: false }); }, 1500);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore avvio conversazione. Invia prima un messaggio dal telefono.'); }
  };

  const handleDisconnect = async () => { try { await api.post('/whatsapp/disconnect'); toast.success('WhatsApp disconnesso'); setStatus({ isReady: false, hasQR: false }); setChats([]); setSelectedChat(null); setPairingCode(null); setPairingMode(false); } catch { toast.error('Errore disconnessione'); } };

  const handleRequestPairingCode = async () => {
    const cleaned = pairingPhone.replace(/[^0-9]/g, '');
    if (cleaned.length < 10) { toast.error('Inserisci un numero valido con prefisso'); return; }
    setPairingLoading(true); setPairingCode(null);
    try {
      const res = await api.post('/whatsapp/pair-with-phone', { phoneNumber: cleaned });
      if (res.data.code) { setPairingCode(res.data.code); toast.success('Codice generato! Inseriscilo su WhatsApp'); }
      else if (res.data.message) toast.info(res.data.message);
      else toast.error('Nessun codice ricevuto');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore generazione codice'); }
    finally { setPairingLoading(false); }
  };

  const formatTime = (ts) => { const d = new Date(ts * 1000); if (isToday(d)) return format(d, 'HH:mm'); if (isYesterday(d)) return 'Ieri ' + format(d, 'HH:mm'); return format(d, 'dd/MM HH:mm'); };
  const groupByDate = (msgs) => { const g = {}; msgs.forEach(m => { const d = new Date(m.timestamp * 1000); let k; if (isToday(d)) k = 'Oggi'; else if (isYesterday(d)) k = 'Ieri'; else k = format(d, 'dd MMMM yyyy', { locale: it }); if (!g[k]) g[k] = []; g[k].push(m); }); return g; };

  // Cache: numeri di telefono dei clienti con scadenze nei prossimi 60gg
  const [scadenzePhones, setScadenzePhones] = useState({}); // {numero_normalizzato: {nominativo, data_scadenza, compagnia}}
  useEffect(() => {
    let cancelled = false;
    const loadScadenzePhones = async () => {
      try {
        const r = await api.get('/scadenze');
        if (cancelled) return;
        const map = {};
        const today = new Date();
        const cutoff = new Date(); cutoff.setDate(today.getDate() + 60);
        (r.data || []).forEach(s => {
          if (!s.cellulare) return;
          let dt = null;
          const ds = s.data_scadenza || '';
          const m1 = ds.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
          const m2 = ds.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (m1) dt = new Date(parseInt(m1[3]), parseInt(m1[2]) - 1, parseInt(m1[1]));
          else if (m2) dt = new Date(parseInt(m2[1]), parseInt(m2[2]) - 1, parseInt(m2[3]));
          if (!dt || dt < today || dt > cutoff) return;
          // Normalizza numero
          const num = String(s.cellulare).replace(/[^0-9]/g, '').replace(/^00/, '').replace(/^39/, '');
          map[num] = { nominativo: s.nominativo, data_scadenza: ds, compagnia: s.compagnia };
        });
        setScadenzePhones(map);
      } catch {}
    };
    loadScadenzePhones();
  }, []);

  const filteredChats = chats.filter(c => {
    if (String(c.id || '').includes('broadcast')) return false;
    if (filter === 'mine' && c.assignedTo !== user?.id) return false;
    if (filter === 'unassigned' && c.assignedTo) return false;
    if (filter === 'rinnovi') {
      // Mostra solo chat dei numeri presenti in scadenze (prossimi 60gg)
      const num = String(c.id || '').split('@')[0].replace(/[^0-9]/g, '').replace(/^00/, '').replace(/^39/, '');
      if (!scadenzePhones[num]) return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase().trim();
    const qDigits = q.replace(/[^0-9]/g, '');
    const cn = contactsMap[c.id]?.nome?.toLowerCase() || '';
    const idDigits = String(c.id || '').replace(/[^0-9]/g, '');
    const numDigits = String(c.number || '').replace(/[^0-9]/g, '');
    return c.name?.toLowerCase().includes(q) || cn.includes(q) || c.id?.includes(q) || (qDigits && (idDigits.includes(qDigits) || numDigits.includes(qDigits)));
  }).sort((a, b) => {
    const ta = (a.lastMessage?.timestamp || a.timestamp || 0);
    const tb = (b.lastMessage?.timestamp || b.timestamp || 0);
    return tb - ta;
  });

  const chatCounts = {
    mine: chats.filter(c => c.assignedTo === user?.id).length,
    unassigned: chats.filter(c => !c.assignedTo).length,
    all: chats.length,
    rinnovi: chats.filter(c => {
      const num = String(c.id || '').split('@')[0].replace(/[^0-9]/g, '').replace(/^00/, '').replace(/^39/, '');
      return !!scadenzePhones[num];
    }).length,
  };

  // Loading
  if (loading) return (
    <div className="flex items-center justify-center h-screen" style={{ backgroundColor: C.bg }} data-testid="whatsapp-loading">
      <div className="text-center"><div className="h-10 w-10 border-2 rounded-full animate-spin mx-auto mb-3" style={{ borderColor: C.primary, borderTopColor: 'transparent' }} /><p className="text-sm" style={{ color: C.textSec, fontFamily: 'Manrope' }}>Connessione a WhatsApp...</p></div>
    </div>
  );

  // QR Code / Pairing Code
  if (!status.isReady && (qrCode || pairingMode)) return (
    <div className="flex items-center justify-center h-screen" style={{ backgroundColor: C.bg }} data-testid="whatsapp-qr-screen">
      <div className="rounded-2xl shadow-lg p-8 max-w-md w-full text-center" style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}>
        <div className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: C.surfaceActive }}><MessageCircle size={28} style={{ color: C.primary }} /></div>
        <h2 className="text-xl font-bold mb-1" style={{ color: C.text, fontFamily: 'Outfit' }}>Connetti WhatsApp</h2>

        {!pairingMode ? (
          <>
            <p className="text-sm mb-6" style={{ color: C.textSec, fontFamily: 'Manrope' }}>Scansiona il QR Code con il tuo telefono</p>
            <div className="inline-block p-3 rounded-2xl mb-4" style={{ backgroundColor: C.surface, border: `2px solid ${C.border}` }}><img src={qrCode} alt="QR Code" className="w-56 h-56" data-testid="qr-code-img" /></div>
            <div className="rounded-xl p-4 text-left mb-4" style={{ backgroundColor: C.surfaceHover }}>
              <p className="text-xs font-semibold mb-2" style={{ color: C.text, fontFamily: 'Outfit' }}>Come fare:</p>
              <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: C.textSec, fontFamily: 'Manrope' }}>
                <li>Apri WhatsApp sul telefono</li><li>Vai su Dispositivi collegati</li><li>Tocca "Collega un dispositivo"</li><li>Scansiona questo QR code</li>
              </ol>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={checkStatus} variant="ghost" size="sm" style={{ color: C.textSec }} data-testid="refresh-qr-btn"><RefreshCw size={14} className="mr-1.5" /> Aggiorna</Button>
              <Button onClick={() => setPairingMode(true)} variant="outline" size="sm" style={{ borderColor: C.primary, color: C.primary }} data-testid="switch-to-pairing-btn"><Hash size={14} className="mr-1.5" /> Usa codice numerico</Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm mb-6" style={{ color: C.textSec, fontFamily: 'Manrope' }}>Collegati inserendo un codice numerico su WhatsApp</p>
            {pairingCode ? (
              <div className="mb-6">
                <div className="inline-block px-8 py-4 rounded-2xl mb-3" style={{ backgroundColor: C.surfaceActive, border: `2px solid ${C.primary}` }}>
                  <span className="text-3xl font-mono font-bold tracking-[0.3em]" style={{ color: C.primary }} data-testid="pairing-code-display">{pairingCode}</span>
                </div>
                <p className="text-xs" style={{ color: C.textSec }}>Inserisci questo codice su WhatsApp &gt; Dispositivi collegati &gt; Collega con numero di telefono</p>
              </div>
            ) : (
              <div className="mb-6 space-y-3">
                <div>
                  <Label className="text-xs text-left block mb-1.5" style={{ color: C.textSec }}>Numero WhatsApp (con prefisso)</Label>
                  <Input value={pairingPhone} onChange={e => setPairingPhone(e.target.value)} placeholder="+39 333 123 4567" className="text-center text-lg rounded-xl" data-testid="pairing-phone-input" style={{ borderColor: C.border }} />
                </div>
                <Button onClick={handleRequestPairingCode} disabled={pairingLoading} className="w-full rounded-xl" style={{ backgroundColor: C.primary, color: C.textInv }} data-testid="request-pairing-btn">
                  {pairingLoading ? <><RefreshCw size={14} className="mr-2 animate-spin" /> Generazione...</> : <><Hash size={14} className="mr-2" /> Genera Codice</>}
                </Button>
              </div>
            )}
            <div className="flex items-center justify-center gap-3">
              <Button onClick={() => { setPairingMode(false); setPairingCode(null); }} variant="ghost" size="sm" style={{ color: C.textSec }} data-testid="switch-to-qr-btn"><ArrowLeft size={14} className="mr-1.5" /> Torna al QR Code</Button>
              {pairingCode && <Button onClick={() => { setPairingCode(null); }} variant="outline" size="sm" style={{ borderColor: C.primary, color: C.primary }} data-testid="new-pairing-code-btn"><RefreshCw size={14} className="mr-1.5" /> Nuovo codice</Button>}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Waiting
  if (!status.isReady) return (
    <div className="flex items-center justify-center h-screen" style={{ backgroundColor: C.bg }} data-testid="whatsapp-connecting">
      <div className="text-center">
        <div className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse" style={{ backgroundColor: '#FEF3C7' }}><WifiOff size={28} className="text-amber-500" /></div>
        <h2 className="text-lg font-bold mb-1" style={{ color: C.text, fontFamily: 'Outfit' }}>In attesa di connessione</h2>
        <p className="text-sm mb-4" style={{ color: C.textSec }}>Attendere il QR code...</p>
        <Button onClick={checkStatus} variant="outline" size="sm" data-testid="refresh-status-btn"><RefreshCw size={14} className="mr-2" /> Riprova</Button>
      </div>
    </div>
  );

  const grouped = groupByDate(messages);

  const FilterTab = ({ value, label, count }) => {
    const active = filter === value;
    return (
      <button onClick={() => setFilter(value)} data-testid={`filter-${value}`}
        className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-lg transition-all active:scale-95"
        style={{
          backgroundColor: active ? C.surface : 'transparent',
          color: active ? C.text : C.textSec,
          boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
          fontFamily: 'Manrope',
        }}>
        <span className="text-[12px] font-semibold leading-none">{label}</span>
        <span className="text-[10px] font-bold leading-none" style={{ color: active ? C.primary : '#A8A29E' }}>{count}</span>
      </button>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex h-[calc(100dvh-56px)] overflow-hidden p-2 sm:p-3 gap-2 sm:gap-3" style={{ backgroundColor: C.bg, fontFamily: 'Manrope, sans-serif' }} data-testid="whatsapp-main">

        {/* ─── Left Panel ─── */}
        <div className={`w-full sm:w-[320px] flex flex-col flex-shrink-0 rounded-2xl overflow-hidden ${selectedChat ? 'hidden sm:flex' : 'flex'}`} style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}>
          {/* Header */}
          <div className="px-4 pt-5 pb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-lg font-semibold tracking-tight" style={{ color: C.text, fontFamily: 'Outfit' }}>Messaggi</h1>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ backgroundColor: C.primary }} />
                  <span className="text-[11px]" style={{ color: C.textSec }}>{chats.length} conversazioni</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Tooltip><TooltipTrigger asChild>
                  <button onClick={() => setNewContactOpen(true)} className="p-2 rounded-xl transition-colors" style={{ color: C.textSec }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.surfaceHover; e.currentTarget.style.color = C.text; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = C.textSec; }} data-testid="new-contact-btn"><Plus size={18} /></button>
                </TooltipTrigger><TooltipContent>Nuova conversazione</TooltipContent></Tooltip>
                {isAdmin && (
                  <Tooltip><TooltipTrigger asChild>
                    <button onClick={handleDisconnect} className="p-2 rounded-xl transition-colors" style={{ color: C.textSec }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.surfaceHover; e.currentTarget.style.color = C.text; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = C.textSec; }} data-testid="disconnect-btn"><LogOut size={16} /></button>
                  </TooltipTrigger><TooltipContent>Disconnetti</TooltipContent></Tooltip>
                )}
              </div>
            </div>
            {/* Search */}
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#A8A29E' }} />
              <input placeholder="Cerca conversazione..." value={searchQuery} onChange={(e) => {
                setSearchQuery(e.target.value);
                // Debounced server search for numbers not in current list
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = setTimeout(() => {
                  const q = e.target.value.trim();
                  if (q.length >= 3) loadChats(q);
                  else if (q.length === 0) loadChats();
                }, 500);
              }}
                className="w-full pl-9 pr-3 h-10 rounded-xl text-sm outline-none transition-all" data-testid="chat-search-input"
                style={{ backgroundColor: C.surfaceHover, border: 'none', color: C.text, fontFamily: 'Manrope' }} />
            </div>
            {/* Filters */}
            <div className="flex gap-1 p-1 rounded-xl" style={{ backgroundColor: C.surfaceHover }}>
              <FilterTab value="mine" label="Mie" count={chatCounts.mine} />
              <FilterTab value="unassigned" label="Libere" count={chatCounts.unassigned} />
              <FilterTab value="rinnovi" label="Rinnovi" count={chatCounts.rinnovi} />
              <FilterTab value="all" label="Tutte" count={chatCounts.all} />
            </div>
          </div>

          {/* Chat List */}
          <ScrollArea className="flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block">
            {!chatsLoaded ? (
              <div className="px-2 py-2 space-y-2" data-testid="chats-skeleton">
                {[0,1,2,3,4,5].map(i => (
                  <div key={i} className="flex items-center gap-3 px-2 py-2">
                    <div className="h-11 w-11 rounded-full animate-pulse flex-shrink-0" style={{ backgroundColor: '#EEECEA' }} />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 rounded animate-pulse" style={{ width: '60%', backgroundColor: '#EEECEA' }} />
                      <div className="h-2.5 rounded animate-pulse" style={{ width: '85%', backgroundColor: '#F2F0EE' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredChats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="h-14 w-14 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: C.surfaceHover }}><MessageCircle size={24} style={{ color: '#A8A29E' }} /></div>
                <p className="text-sm font-medium" style={{ color: C.textSec }}>Nessuna conversazione</p>
                <p className="text-xs mt-1" style={{ color: '#A8A29E' }}>{filter === 'mine' ? 'Prendi in carico una chat dalla tab "Libere"' : searchQuery ? 'Nessun risultato per la ricerca' : 'Non ci sono chat disponibili'}</p>
              </div>
            ) : (
              <div className="px-1 pb-2">
                {filteredChats.map(chat => (
                  <ChatListItem key={chat.id} chat={chat} isSelected={selectedChat?.id === chat.id} onClick={() => setSelectedChat(chat)} currentUserId={user?.id} contactName={contactsMap[chat.id]?.nome} showAssignment={true} />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* ─── Center: Chat Area ─── */}
        <div className={`flex-1 flex flex-col min-w-0 rounded-2xl overflow-hidden ${!selectedChat ? 'hidden sm:flex' : 'flex'}`} style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}>
          {selectedChat ? (
            <>
              {/* Chat Header - Glassmorphism */}
              <div className="px-5 py-3.5 flex items-center justify-between flex-shrink-0 sticky top-0 z-10 backdrop-blur-xl" style={{ backgroundColor: 'rgba(255,255,255,0.85)', borderBottom: `1px solid ${C.border}` }}>
                <div className="flex items-center gap-3 min-w-0">
                  <button className="sm:hidden p-1.5 rounded-xl transition-colors" style={{ color: C.textSec }} onClick={() => setSelectedChat(null)} data-testid="back-to-chats-btn"><ArrowLeft size={18} /></button>
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <AvatarFallback className="text-sm font-semibold" style={{ backgroundColor: C.surfaceActive, color: C.primary, fontFamily: 'Manrope' }}>
                      {getContactDisplayName(selectedChat)?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold truncate" style={{ color: C.text, fontFamily: 'Outfit' }}>{getContactDisplayName(selectedChat)}</h2>
                    <p className="text-[11px] truncate" style={{ color: C.textSec }} data-testid="chat-header-number">{selectedChat.isGroup ? 'Gruppo' : getChatNumberLabel(selectedChat.id)}</p>
                    {/* Chat Note */}
                    {chatNoteEditing ? (
                      <div className="flex items-center gap-1 mt-1">
                        <input value={chatNoteDraft} onChange={e => setChatNoteDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveChatNote(); if (e.key === 'Escape') setChatNoteEditing(false); }}
                          className="text-[11px] px-2 py-0.5 rounded-md flex-1 outline-none" style={{ backgroundColor: C.surfaceHover, color: C.text, border: `1px solid ${C.primary}` }}
                          placeholder="Es: Rinnovo polizza Auto, Sostituzione..." autoFocus data-testid="chat-note-input" />
                        <button onClick={saveChatNote} className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: C.primary, color: C.textInv }} data-testid="chat-note-save">OK</button>
                        <button onClick={() => { setChatNoteEditing(false); setChatNoteDraft(chatNote); }} className="text-[10px] px-1 py-0.5" style={{ color: C.textSec }}>✕</button>
                      </div>
                    ) : chatNote ? (
                      <p onClick={() => setChatNoteEditing(true)} className="text-[11px] mt-0.5 cursor-pointer truncate max-w-[250px] hover:opacity-80" style={{ color: C.primary, fontStyle: 'italic' }} title={chatNote} data-testid="chat-note-display">
                        {chatNote}
                      </p>
                    ) : (
                      <button onClick={() => setChatNoteEditing(true)} className="text-[10px] mt-0.5 opacity-50 hover:opacity-80" style={{ color: C.textSec }} data-testid="chat-note-add">+ Aggiungi nota</button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {!selectedChat.assignedTo ? (
                    <button onClick={() => handleAssign(selectedChat.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-medium transition-colors" style={{ backgroundColor: C.primary, color: C.textInv }} data-testid="assign-chat-btn">
                      <Headphones size={13} /> Prendi in carico
                    </button>
                  ) : (selectedChat.assignedTo === user?.id || isAdmin) ? (
                    <>
                      <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-medium" style={{ backgroundColor: C.surfaceActive, color: C.primary }}>
                        <User size={11} /> {selectedChat.assignedTo === user?.id ? 'Tu' : selectedChat.assignedToName}
                      </span>
                      <button onClick={() => handleUnassign(selectedChat.id)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-medium transition-colors" style={{ border: `1px solid ${C.border}`, color: C.textSec }} data-testid="unassign-chat-btn">
                        <UserMinus size={13} /> Rilascia
                      </button>
                    </>
                  ) : selectedChat.assignedTo ? (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium" style={{ backgroundColor: C.surfaceActive, color: C.primary }}><User size={11} /> {selectedChat.assignedToName}</span>
                  ) : null}
                  <Tooltip><TooltipTrigger asChild>
                    <button className="hidden sm:flex p-2 rounded-xl transition-colors" style={{ color: C.textSec }} onClick={() => setShowContactPanel(!showContactPanel)} onMouseEnter={e => e.currentTarget.style.backgroundColor = C.surfaceHover} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'} data-testid="toggle-contact-panel-btn"><Info size={16} /></button>
                  </TooltipTrigger><TooltipContent>Info contatto</TooltipContent></Tooltip>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4" style={{ backgroundColor: C.bg }} data-testid="messages-area">
                {msgLoading && messages.length === 0 ? (
                  <div className="space-y-3 py-4" data-testid="messages-skeleton">
                    {[0,1,2,3,4].map(i => (
                      <div key={i} className={`flex ${i % 2 ? 'justify-end' : 'justify-start'}`}>
                        <div className="h-10 rounded-2xl animate-pulse" style={{ width: `${40 + (i * 11) % 40}%`, backgroundColor: i % 2 ? '#DCE3DF' : '#EEECEA' }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  Object.entries(grouped).map(([date, msgs]) => (
                    <div key={date}>
                      <div className="flex justify-center my-4">
                        <span className="px-4 py-1 rounded-full text-[11px] font-medium shadow-sm" style={{ backgroundColor: C.surface, color: C.textSec, border: `1px solid ${C.border}` }}>{date}</span>
                      </div>
                      {msgs.map((msg, i) => <MessageBubble key={`${date}-${i}-${msg.id || ''}`} msg={msg} formatTime={formatTime} onImageClick={setLightboxSrc} chatId={selectedChat?.id} onRetry={retrySend} />)}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="px-4 sm:px-6 py-3 flex-shrink-0" style={{ borderTop: `1px solid ${C.border}`, backgroundColor: C.surface }}>
                {selectedFile && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-xl" style={{ backgroundColor: C.surfaceActive, border: `1px solid ${C.border}` }}>
                    <Paperclip size={14} style={{ color: C.primary }} />
                    <span className="text-xs flex-1 truncate" style={{ color: C.text }}>{selectedFile.name}</span>
                    <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} style={{ color: C.textSec }} data-testid="remove-file-btn"><X size={14} /></button>
                  </div>
                )}
                {quickOpen && (
                  <div className="mb-2 rounded-xl p-2 space-y-1" style={{ backgroundColor: C.surfaceHover, border: `1px solid ${C.border}` }} data-testid="quick-replies-list">
                    <p className="text-[10px] uppercase tracking-wider font-semibold px-1 mb-1" style={{ color: C.textSec }}>Risposte rapide</p>
                    {QUICK_REPLIES.map((q, i) => (
                      <button key={i} type="button" onClick={() => { setNewMessage(q.text); setQuickOpen(false); }} className="w-full text-left px-3 py-2 rounded-lg text-xs transition-colors" style={{ backgroundColor: C.surface, color: C.text, border: `1px solid ${C.border}` }} data-testid={`quick-reply-${i}`} onMouseEnter={e => e.currentTarget.style.backgroundColor = C.surfaceActive} onMouseLeave={e => e.currentTarget.style.backgroundColor = C.surface}>
                        <span className="font-semibold" style={{ color: C.primary }}>{q.label}</span>
                        <span className="block truncate mt-0.5" style={{ color: C.textSec }}>{q.text}</span>
                      </button>
                    ))}
                  </div>
                )}
                <form onSubmit={handleSend} className="flex items-end gap-2">
                  <input ref={fileInputRef} type="file" accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" onChange={(e) => setSelectedFile(e.target.files[0])} className="hidden" />
                  <Tooltip><TooltipTrigger asChild>
                    <button type="button" onClick={() => setQuickOpen(o => !o)} className="p-2.5 rounded-xl transition-colors flex-shrink-0" style={{ color: quickOpen ? C.primary : C.textSec, backgroundColor: quickOpen ? C.surfaceActive : 'transparent' }} data-testid="quick-replies-btn"><MessageSquareText size={18} /></button>
                  </TooltipTrigger><TooltipContent>Risposte rapide</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2.5 rounded-xl transition-colors flex-shrink-0" style={{ color: C.textSec }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.surfaceHover; e.currentTarget.style.color = C.text; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = C.textSec; }} data-testid="attach-file-btn"><Paperclip size={18} /></button>
                  </TooltipTrigger><TooltipContent>Allega file</TooltipContent></Tooltip>
                  <div className="flex-1 rounded-2xl overflow-hidden transition-all" style={{ border: `1px solid ${C.border}`, backgroundColor: C.surface }}>
                    <textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }} placeholder="Scrivi un messaggio..." rows={1} className="w-full px-4 py-2.5 text-sm outline-none bg-transparent resize-none block" style={{ color: C.text, fontFamily: 'Manrope', minHeight: '44px', maxHeight: '140px' }} data-testid="message-input" onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px'; }} />
                  </div>
                  <button type="submit" disabled={!newMessage.trim() && !selectedFile} className="p-3 rounded-xl transition-all flex-shrink-0 disabled:opacity-40 active:scale-95" style={{ backgroundColor: C.primary, color: C.textInv }} data-testid="send-message-btn"><Send size={18} /></button>
                </form>
              </div>
            </>
          ) : (
            <div className="hidden sm:flex flex-1 items-center justify-center" style={{ backgroundColor: C.bg }} data-testid="no-chat-selected">
              <div className="text-center max-w-sm">
                <div className="h-20 w-20 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: C.surfaceActive }}>
                  <MessageCircle size={36} style={{ color: C.primary }} />
                </div>
                <h2 className="text-xl font-semibold mb-2" style={{ color: C.text, fontFamily: 'Outfit' }}>Seleziona una conversazione</h2>
                <p className="text-sm leading-relaxed" style={{ color: C.textSec }}>Scegli una chat dalla lista per gestire le richieste dei clienti.</p>
              </div>
            </div>
          )}
        </div>

        {/* ─── Right Panel ─── */}
        {showContactPanel && selectedChat && (
          <div className="w-[280px] flex-shrink-0 hidden lg:flex flex-col rounded-2xl overflow-hidden" style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }} data-testid="contact-info-panel">
            <div className="px-4 py-3.5 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
              <h3 className="text-sm font-semibold" style={{ color: C.text, fontFamily: 'Outfit' }}>Info Contatto</h3>
              <button className="p-1.5 rounded-lg transition-colors" style={{ color: C.textSec }} onClick={() => setShowContactPanel(false)} onMouseEnter={e => e.currentTarget.style.backgroundColor = C.surfaceHover} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}><X size={14} /></button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-6 text-center" style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: C.surfaceHover }}>
                <Avatar className="h-20 w-20 mx-auto mb-3">
                  <AvatarFallback className="text-2xl font-bold" style={{ backgroundColor: C.surfaceActive, color: C.primary, fontFamily: 'Outfit' }}>
                    {getContactDisplayName(selectedChat)?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'}
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-base font-semibold" style={{ color: C.text, fontFamily: 'Outfit' }}>{getContactDisplayName(selectedChat)}</h3>
                <p className="text-xs mt-0.5" style={{ color: C.textSec }}>{selectedChat.isGroup ? 'Gruppo' : getChatNumberLabel(selectedChat.id)}</p>
              </div>

              <div className="p-4 space-y-4">
                {/* Rubrica */}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2 flex items-center gap-1.5" style={{ color: C.textSec }}><BookUser size={12} /> Rubrica</p>
                  {isEditingContact ? (
                    <div className="space-y-2">
                      <Input value={editingContactName} onChange={(e) => setEditingContactName(e.target.value)} placeholder="Nome contatto..." className="h-8 text-xs rounded-xl" onKeyDown={(e) => e.key === 'Enter' && saveContactName(selectedChat.id, editingContactName)} autoFocus data-testid="contact-name-input" style={{ borderColor: C.border, fontFamily: 'Manrope' }} />
                      <div className="flex gap-1.5">
                        <button className="flex-1 flex items-center justify-center gap-1 h-7 text-xs font-medium rounded-xl transition-colors" style={{ backgroundColor: C.primary, color: C.textInv }} onClick={() => saveContactName(selectedChat.id, editingContactName)} data-testid="save-contact-name-btn"><Save size={12} /> Salva</button>
                        <button className="h-7 px-2.5 text-xs rounded-xl transition-colors" style={{ border: `1px solid ${C.border}`, color: C.textSec }} onClick={() => setIsEditingContact(false)} data-testid="cancel-contact-edit-btn"><X size={12} /></button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {contactsMap[selectedChat.id]?.nome ? (
                        <div className="flex items-center gap-2 p-2.5 rounded-xl" style={{ backgroundColor: C.surfaceActive, border: `1px solid ${C.border}` }}>
                          <BookUser size={14} style={{ color: C.primary }} className="flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate" style={{ color: C.primary }}>{contactsMap[selectedChat.id].nome}</p>
                            <p className="text-[10px]" style={{ color: C.textSec }}>da {contactsMap[selectedChat.id].updated_by_nome}</p>
                          </div>
                          <div className="flex gap-0.5 flex-shrink-0">
                            <button onClick={() => { setEditingContactName(contactsMap[selectedChat.id].nome); setIsEditingContact(true); }} className="p-1 rounded" style={{ color: C.textSec }} data-testid="edit-contact-name-btn"><Edit3 size={12} /></button>
                            <button onClick={() => deleteContactName(selectedChat.id)} className="p-1 rounded hover:text-red-500" style={{ color: C.textSec }} data-testid="delete-contact-name-btn"><Trash2 size={12} /></button>
                          </div>
                        </div>
                      ) : (
                        <button className="w-full flex items-center gap-2 h-8 px-3 text-xs rounded-xl transition-colors" style={{ border: `1px solid ${C.border}`, color: C.textSec }} onClick={() => { setEditingContactName(''); setIsEditingContact(true); }} data-testid="add-contact-name-btn"><Edit3 size={12} /> Assegna nome</button>
                      )}
                    </div>
                  )}
                </div>
                <Separator style={{ backgroundColor: C.border }} />
                {/* Assegnazione */}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: C.textSec }}>Stato</p>
                  {selectedChat.assignedTo ? (
                    <div className="flex items-center gap-2 p-2.5 rounded-xl" style={{ backgroundColor: C.surfaceActive }}>
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: C.primary }} />
                      <span className="text-xs font-medium" style={{ color: C.primary }}>{selectedChat.assignedTo === user?.id ? 'Gestita da te' : `Gestita da ${selectedChat.assignedToName}`}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-2.5 rounded-xl" style={{ backgroundColor: '#FEF3C7' }}>
                      <span className="h-2 w-2 rounded-full bg-amber-500" />
                      <span className="text-xs font-medium text-amber-700">Non assegnata</span>
                    </div>
                  )}
                </div>
                <Separator style={{ backgroundColor: C.border }} />
                {/* Azioni */}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: C.textSec }}>Azioni</p>
                  <div className="space-y-1.5">
                    {!selectedChat.assignedTo && (
                      <button className="w-full flex items-center gap-2 h-8 px-3 text-xs font-medium rounded-xl transition-colors" style={{ backgroundColor: C.primary, color: C.textInv }} onClick={() => handleAssign(selectedChat.id)} data-testid="sidebar-assign-btn"><Headphones size={14} /> Prendi in Carico</button>
                    )}
                    {(selectedChat.assignedTo === user?.id || isAdmin) && selectedChat.assignedTo && (
                      <button className="w-full flex items-center gap-2 h-8 px-3 text-xs font-medium rounded-xl transition-colors" style={{ border: `1px solid ${C.border}`, color: C.textSec }} onClick={() => handleUnassign(selectedChat.id)} data-testid="sidebar-unassign-btn"><UserMinus size={14} /> Rilascia Chat</button>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <>
                    <Separator style={{ backgroundColor: C.border }} />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: C.textSec }}>Assegna a</p>
                      <div className="space-y-1">
                        {users.map(u => (
                          <button key={u.id} onClick={async () => { try { await api.post(`/whatsapp/chat/assign?chat_id=${selectedChat.id}&user_id=${u.id}`); toast.success(`Chat assegnata a ${u.nome}`); setSelectedChat({ ...selectedChat, assignedTo: u.id, assignedToName: `${u.nome} ${u.cognome}` }); loadChats(); } catch { toast.error('Errore assegnazione'); } }}
                            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors text-xs"
                            style={{ backgroundColor: selectedChat.assignedTo === u.id ? C.surfaceActive : 'transparent', color: selectedChat.assignedTo === u.id ? C.primary : C.textSec }}
                            data-testid={`assign-to-${u.id}`}>
                            <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px]" style={{ backgroundColor: C.surfaceHover }}>{u.nome?.[0]}{u.cognome?.[0]}</AvatarFallback></Avatar>
                            <span>{u.nome} {u.cognome}</span>
                            {selectedChat.assignedTo === u.id && <Check size={14} className="ml-auto" style={{ color: C.primary }} />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                <Separator style={{ backgroundColor: C.border }} />
                {/* Note */}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: C.textSec }}>Note Interne</p>
                  <div className="flex gap-1.5 mb-3">
                    <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Scrivi una nota..." className="h-8 text-xs rounded-xl" onKeyDown={(e) => e.key === 'Enter' && addNote()} data-testid="chat-note-input" style={{ borderColor: C.border }} />
                    <button className="h-8 px-2.5 rounded-xl flex-shrink-0 transition-colors" style={{ backgroundColor: C.primary, color: C.textInv }} onClick={addNote} data-testid="add-note-btn"><Plus size={14} /></button>
                  </div>
                  {chatNotes.length === 0 ? (
                    <p className="text-[11px] text-center py-3" style={{ color: '#A8A29E' }}>Nessuna nota</p>
                  ) : (
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {chatNotes.map(note => (
                        <div key={note.id} className="p-2.5 rounded-xl" style={{ backgroundColor: C.noteBg, border: `1px solid ${C.noteBorder}` }} data-testid={`note-${note.id}`}>
                          <div className="flex items-start justify-between gap-1">
                            <p className="text-xs" style={{ color: C.noteText }}>{note.testo}</p>
                            <button onClick={() => deleteNote(note.id)} className="flex-shrink-0 hover:text-red-400" style={{ color: '#D1C4A9' }}><Trash2 size={12} /></button>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <span className="text-[10px] font-medium" style={{ color: C.accent }}>{note.autore_nome}</span>
                            <span className="text-[10px]" style={{ color: '#A8A29E' }}>{note.created_at ? format(new Date(note.created_at), 'dd/MM HH:mm', { locale: it }) : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* New Contact Dialog */}
      <Dialog open={newContactOpen} onOpenChange={setNewContactOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Nuova Conversazione</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm" style={{ fontFamily: 'Manrope' }}>Numero Telefono</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex items-center gap-1.5 px-3 h-10 rounded-xl text-sm flex-shrink-0" style={{ border: `1px solid ${C.border}`, backgroundColor: C.surfaceHover, color: C.textSec }}><Phone size={14} /></div>
                <Input placeholder="+39 333 123 4567" value={newContactNumber} onChange={(e) => setNewContactNumber(e.target.value)} className="rounded-xl" data-testid="new-contact-number-input" />
              </div>
              <p className="text-[11px] mt-1" style={{ color: C.textSec }}>Inserisci il numero con prefisso (+39 per l'Italia)</p>
            </div>
            <div>
              <Label className="text-sm" style={{ fontFamily: 'Manrope' }}>Primo Messaggio</Label>
              <Input placeholder="Buongiorno, sono dell'agenzia Schiliro..." value={newContactMessage} onChange={(e) => setNewContactMessage(e.target.value)} className="mt-1.5 rounded-xl" data-testid="new-contact-message-input" />
              <p className="text-[11px] mt-1" style={{ color: C.textSec }}>Se vuoto, verra' inviato "Buongiorno"</p>
            </div>
            <button onClick={handleNewContact} className="w-full flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-medium transition-colors" style={{ backgroundColor: C.primary, color: C.textInv }} data-testid="start-conversation-btn"><Send size={16} /> Avvia Conversazione</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightboxSrc && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col" data-testid="image-lightbox">
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button onClick={() => setZoomLevel(z => Math.min(z + 0.5, 5))} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors" data-testid="zoom-in-btn"><ZoomIn size={18} /></button>
              <button onClick={() => { setZoomLevel(z => { const nz = Math.max(z - 0.5, 0.5); if (nz <= 1) setPanPos({ x: 0, y: 0 }); return nz; }); }} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors" data-testid="zoom-out-btn"><ZoomOut size={18} /></button>
              <button onClick={() => { setZoomLevel(1); setPanPos({ x: 0, y: 0 }); }} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors" data-testid="zoom-reset-btn"><RotateCcw size={18} /></button>
              <span className="text-white/60 text-xs ml-2">{Math.round(zoomLevel * 100)}%</span>
            </div>
            <div className="flex items-center gap-2">
              <a href={lightboxSrc} download={`foto_whatsapp_${Date.now()}.jpg`}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors"
                data-testid="lightbox-download-btn">
                <FileDown size={16} /> Scarica
              </a>
              <button onClick={() => { setLightboxSrc(null); setZoomLevel(1); setPanPos({ x: 0, y: 0 }); }} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors" data-testid="close-lightbox-btn"><X size={20} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden flex items-center justify-center" style={{ cursor: zoomLevel > 1 ? 'grab' : 'zoom-in' }}
            onClick={(e) => { if (e.target === e.currentTarget && zoomLevel <= 1) setZoomLevel(2); }}
            onWheel={(e) => { e.preventDefault(); setZoomLevel(z => { const nz = Math.max(0.5, Math.min(5, z + (e.deltaY < 0 ? 0.3 : -0.3))); if (nz <= 1) setPanPos({ x: 0, y: 0 }); return nz; }); }}
            onMouseDown={(e) => { if (zoomLevel > 1) { isPanningRef.current = true; panStartRef.current = { x: e.clientX, y: e.clientY, px: panPos.x, py: panPos.y }; e.currentTarget.style.cursor = 'grabbing'; } }}
            onMouseMove={(e) => { if (isPanningRef.current) setPanPos({ x: panStartRef.current.px + (e.clientX - panStartRef.current.x), y: panStartRef.current.py + (e.clientY - panStartRef.current.y) }); }}
            onMouseUp={(e) => { isPanningRef.current = false; if (e.currentTarget) e.currentTarget.style.cursor = zoomLevel > 1 ? 'grab' : 'zoom-in'; }}
            onMouseLeave={() => { isPanningRef.current = false; }}>
            <img src={lightboxSrc} alt="Anteprima" className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl select-none" style={{ transform: `scale(${zoomLevel}) translate(${panPos.x / zoomLevel}px, ${panPos.y / zoomLevel}px)`, transition: isPanningRef.current ? 'none' : 'transform 0.2s ease' }} draggable={false} />
          </div>
          <p className="text-center text-white/40 text-[11px] pb-3">Scroll per zoom &middot; Trascina per muovere</p>
        </div>
      )}
    </TooltipProvider>
  );
};
